import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { ApiAuthService, AuthService, FCMService } from '@shared';
import { Subject, Observable, EMPTY, of, BehaviorSubject, combineLatest } from 'rxjs';
import { switchMap, catchError, map, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { VendorOrdersService } from '../core/services/vendor-orders.service';

const PUSH_DEEPLINK_STORAGE_KEY = 'vendor:push-deeplink';

/** Merge FCM data with fallbacks when Android drops the data payload on notification tap. */
export function normalizeVendorPushData(
  data?: Record<string, string | undefined | null>,
  notification?: { title?: string; body?: string },
): Record<string, string | undefined> {
  const merged: Record<string, string | undefined> = {};
  if (data) {
    for (const [key, value] of Object.entries(data)) {
      if (value != null && value !== '') {
        merged[key] = String(value);
      }
    }
  }
  if (
    merged['orderId'] ||
    merged['order_id'] ||
    merged['orderNumber'] ||
    merged['order_number']
  ) {
    return merged;
  }
  const text = `${notification?.title ?? ''} ${notification?.body ?? ''}`;
  const hashMatch = text.match(/#(\d+)/);
  if (hashMatch) {
    merged['orderNumber'] = hashMatch[1];
  }
  return merged;
}

/** Build orders route from FCM data payload (orderId or orderNumber).
 * Uses `openOrder` (not `orderId`) so the header search box is not activated and
 * Live Orders keeps showing the full pending list.
 */
export function buildVendorOrdersUrlFromPushData(
  data?: Record<string, string | undefined | null>,
): string {
  const orderId = data?.['orderId'] || data?.['order_id'];
  const orderNumber = data?.['orderNumber'] || data?.['order_number'];
  const id = (orderId || orderNumber)?.trim();
  if (id) {
    return `/tabs/orders?openOrder=${encodeURIComponent(id)}`;
  }
  return '/tabs/orders';
}

function isVendorOrderPush(data?: Record<string, string | undefined>): boolean {
  const type = data?.['type'] || data?.['notificationType'];
  if (!type) {
    return true;
  }
  if (type === 'new-order' || type === 'new_order') {
    return true;
  }
  if (type === 'order-reminder') {
    return true;
  }
  if (type === 'order_status_update') {
    return !data['status'] || data['status'] === 'pending';
  }
  return false;
}

// Type definitions for native push notifications (only used when isNative is true)
// These types are defined inline to avoid requiring the module at compile time
interface RegistrationError {
  error: string;
}

interface PushNotificationSchema {
  title?: string;
  body?: string;
  data?: Record<string, any>;
}

interface PushNotificationToken {
  value: string;
}

interface PushNotificationActionPerformed {
  actionId: string;
  notification: PushNotificationSchema;
}

type PushPayload = { notification?: { title?: string; body?: string }; data?: Record<string, string> };

/**
 * Unified push service for the vendor app:
 * - Native (Capacitor): Uses @capacitor/push-notifications (FCM token from native)
 * - Web: Uses Firebase Web SDK (FCMService)
 *
 * Request permission on app startup (before login) so users see the prompt as soon as they open the app.
 */
@Injectable({ providedIn: 'root' })
export class VendorPushService {
  private readonly fcmService = inject(FCMService);
  private readonly authService = inject(AuthService);
  private readonly apiAuthService = inject(ApiAuthService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly profile$ = toObservable(this.authService.profile);
  private readonly destroy$ = new Subject<void>();
  /** Fires when user taps a push/local notification (even if already on the same order route). */
  private readonly orderDeepLinkTap$ = new Subject<string>();

  /**
   * Call once from AppComponent: SW postMessage (web background) + flush deeplink after login.
   */
  setupNotificationNavigationHandlers(): void {
    this.profile$
      .pipe(
        filter((p) => !!p?.id && p.role === 'vendor'),
        distinctUntilChanged((a, b) => a?.id === b?.id),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.consumePendingDeepLink());

    if (typeof window !== 'undefined') {
      window.addEventListener('vendor-open-order', ((event: Event) => {
        const detail = (event as CustomEvent<{ orderId?: string }>).detail;
        const orderId = detail?.orderId?.trim();
        if (orderId) {
          this.handleNotificationTap({ type: 'new-order', orderId });
        } else {
          this.consumePendingDeepLink();
        }
      }) as EventListener);
    }

    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const msg = event.data;
      if (msg?.type === 'VENDOR_NOTIFICATION_CLICK') {
        this.handleNotificationTap(msg.data as Record<string, string> | undefined);
      }
    });
  }

  /** Subscribe from OrdersPage to open the detail modal on every notification tap. */
  onOrderDeepLinkTap(): Observable<string> {
    return this.orderDeepLinkTap$.asObservable();
  }

  /** Navigate to Live Orders when user taps a push notification (native or web). */
  handleNotificationTap(data?: Record<string, string | undefined>): void {
    if (!isVendorOrderPush(data)) {
      return;
    }
    const url = buildVendorOrdersUrlFromPushData(data);
    const orderRef = (
      data?.['orderId'] ||
      data?.['order_id'] ||
      data?.['orderNumber'] ||
      data?.['order_number']
    )?.trim();
    void this.vendorOrdersService.refreshTabCounts();
    this.ngZone.run(() => {
      if (this.canNavigateToOrders()) {
        void this.router.navigateByUrl(url).then(() => {
          // Ensure modal opens even when already on the same openOrder URL (no queryParam emit).
          if (orderRef) {
            this.orderDeepLinkTap$.next(orderRef);
          }
        });
        this.clearPendingDeepLink();
      } else {
        this.storePendingDeepLink(url);
      }
    });
  }

  /**
   * Force-open a specific order modal (e.g. toast "View" while already on Live Orders).
   * Prefer {@link handleNotificationTap} for notification taps.
   */
  requestOpenOrderDetail(orderRef: string): void {
    const key = orderRef?.trim();
    if (!key) {
      return;
    }
    this.orderDeepLinkTap$.next(key);
  }

  consumePendingDeepLink(): void {
    if (!this.canNavigateToOrders()) {
      return;
    }
    const url = this.readPendingDeepLink();
    if (!url) {
      return;
    }
    this.ngZone.run(() => {
      void this.router.navigateByUrl(url);
      this.clearPendingDeepLink();
    });
  }

  private canNavigateToOrders(): boolean {
    return this.apiAuthService.isAuthenticated() && this.authService.profile()?.role === 'vendor';
  }

  private storePendingDeepLink(url: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(PUSH_DEEPLINK_STORAGE_KEY, url);
    } catch {
      // ignore
    }
  }

  private readPendingDeepLink(): string | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      return sessionStorage.getItem(PUSH_DEEPLINK_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private clearPendingDeepLink(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.removeItem(PUSH_DEEPLINK_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Holds the native FCM token when received (native only). */
  private readonly nativeTokenSubject = new BehaviorSubject<string | null>(null);
  private listenersRegistered = false;
  /** True after logout tore down native listeners; login must call {@link ensureRegisteredAfterLogin}. */
  private nativePushTornDown = false;

  /** For diagnostics / delayed UI warnings (e.g. app.component). */
  getNativeTokenPeek(): string | null {
    return this.nativeTokenSubject.value;
  }

  /** Whether we're on native platform (Capacitor). */
  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Tear down native push listeners on logout so foreground FCM cannot drive in-app alerts.
   * Backend unregister is handled separately (while JWT may still be valid).
   */
  async teardownOnLogout(): Promise<void> {
    try {
      if (this.isNative) {
        await (PushNotifications as unknown as { removeAllListeners(): Promise<void> }).removeAllListeners();
        this.listenersRegistered = false;
        this.nativePushTornDown = true;
        this.nativeTokenSubject.next(null);
      }
    } catch (e: unknown) {
      console.warn('[VendorPush] teardownOnLogout failed:', (e as Error)?.message ?? e);
    }
  }

  /**
   * Call after successful login when native push was torn down by logout.
   * Safe no-op when the user never logged out in this session.
   */
  async ensureRegisteredAfterLogin(): Promise<void> {
    try {
      if (!this.isNative) return;
      if (this.nativeTokenSubject.value) {
        this.nativePushTornDown = false;
        return;
      }
      if (!this.nativePushTornDown) return;
      console.log('[FCM-E2E][Vendor] Re-registering native push after logout');
      await this.registerNativePush();
      this.nativePushTornDown = false;
    } catch (e: unknown) {
      console.error('[FCM-E2E][Vendor] ensureRegisteredAfterLogin failed', e);
    }
  }

  /**
   * Request notification permission and set up push. Call on app startup (before login).
   * On native: Shows system permission dialog.
   * On web: FCM permission is requested when user is authenticated (Firebase requires it).
   */
  async requestPermissionOnStartup(): Promise<void> {
    if (!this.isNative) {
      console.log('[VendorPush] Web platform: FCM permission requested after login');
      return;
    }

    try {
      const permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
        console.log('[FCM-E2E][Vendor] requesting notification permission on startup…');
        const result = await PushNotifications.requestPermissions();
        if (result.receive === 'granted') {
          console.log('[FCM-E2E][Vendor] permission granted → registerNativePush');
          await this.registerNativePush();
        } else {
          console.warn('[VendorPush] Permission denied:', result.receive);
        }
      } else if (permStatus.receive === 'granted') {
        console.log('[FCM-E2E][Vendor] permission already granted → registerNativePush');
        await this.registerNativePush();
      } else {
        console.warn('[VendorPush] Permission denied:', permStatus.receive);
      }
    } catch (err) {
      console.error('[VendorPush] Startup permission/registration failed:', err);
    }
  }

  /**
   * Register for native push and add listeners. Token is stored in nativeTokenSubject.
   * Requires google-services.json in android-vendor/app/ for FCM to work.
   */
  private async registerNativePush(): Promise<void> {
    if (!this.listenersRegistered) {
      await this.addNativeListeners();
    }
    try {
      const REGISTER_TIMEOUT_MS = 15000;
      await Promise.race([
        PushNotifications.register(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('FCM register timeout')), REGISTER_TIMEOUT_MS),
        ),
      ]);
      // Token often arrives on the registration listener after register() resolves; some devices are slow.
      void this.scheduleNativeRegisterFallback();
    } catch (err) {
      console.error('[FCM-E2E][Vendor] PushNotifications.register failed or timed out (check android-vendor/app/google-services.json)', err);
      // Do not throw: startup already completed; retryNativePushIfNeeded runs after login / resume.
    }
  }

  /**
   * If no token appears shortly after a successful register(), call register() once more.
   * Mitigates races where FCM is slow or the first call did not surface the listener yet.
   */
  private async scheduleNativeRegisterFallback(): Promise<void> {
    if (!this.isNative) return;
    await new Promise((r) => setTimeout(r, 2500));
    if (this.nativeTokenSubject.value) return;
    try {
      const perm = await PushNotifications.checkPermissions();
      if (perm.receive !== 'granted') return;
      console.warn('[VendorPush] No FCM token ~2.5s after register() — retrying PushNotifications.register()');
      await PushNotifications.register();
    } catch (e: any) {
      console.warn('[VendorPush] FCM register fallback failed:', e?.message ?? e);
    }
  }

  /**
   * Call after the user session is active (login or restored profile) or when returning from background.
   * Cold-start FCM registration may time out, run before permission is granted, or race the WebView — this gives another chance.
   */
  async retryNativePushIfNeeded(context: string): Promise<void> {
    if (!this.isNative) return;
    try {
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        console.log(`[VendorPush] retry (${context}): requesting notification permission…`);
        perm = await PushNotifications.requestPermissions();
      }
      if (perm.receive !== 'granted') {
        console.warn(
          `[VendorPush] retry (${context}): notifications permission is "${perm.receive}" — enable in system settings to receive pushes`,
        );
        return;
      }
      if (this.nativeTokenSubject.value) {
        return;
      }
      console.log(`[VendorPush] retry (${context}): registering for FCM (no token yet)…`);
      await this.registerNativePush();
    } catch (e: any) {
      console.warn(`[VendorPush] retry (${context}) failed:`, e?.message ?? e);
    }
  }

  private async addNativeListeners(): Promise<void> {
    if (this.listenersRegistered) return;
    this.listenersRegistered = true;

    await PushNotifications.addListener('registration', (token: PushNotificationToken) => {
      const v = token?.value ?? '';
      console.log(
        `[FCM-E2E][Vendor] native FCM token received len=${v.length} preview=${v.length > 16 ? v.slice(0, 12) + '…' : v || '(empty)'}`,
      );
      this.nativeTokenSubject.next(token.value);
    });

    await PushNotifications.addListener('registrationError', (err: RegistrationError) => {
      console.error('[FCM-E2E][Vendor] native registrationError:', err.error);
      this.nativeTokenSubject.next(null);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      const t = (notification.data as Record<string, string> | undefined)?.['type'];
      console.log(
        `[FCM-E2E][Vendor] foreground push type=${t ?? '(none)'} title=${notification.title ?? ''}`,
      );
      this.foregroundPayload$.next({
        notification: notification.title ? { title: notification.title, body: notification.body ?? '' } : undefined,
        data: notification.data as Record<string, string> | undefined,
      });
    });

    const pushPlugin = PushNotifications as unknown as {
      addListener(
        ev: 'pushNotificationActionPerformed',
        cb: (action: PushNotificationActionPerformed) => void,
      ): Promise<void>;
    };
    await pushPlugin.addListener('pushNotificationActionPerformed', (action) => {
      const data = normalizeVendorPushData(
        action.notification.data as Record<string, string> | undefined,
        { title: action.notification.title, body: action.notification.body },
      );
      console.log(
        `[FCM-E2E][Vendor] user tapped notification type=${data?.['type'] ?? '(none)'} orderId=${data?.['orderId'] ?? data?.['orderNumber'] ?? '(none)'}`,
      );
      this.handleNotificationTap(data);
    });
  }

  private foregroundPayload$ = new Subject<PushPayload>();

  /** Observable of foreground push payloads (works on both native and web). */
  onForegroundMessage(): Observable<PushPayload> {
    if (this.isNative) {
      return this.foregroundPayload$.asObservable();
    }
    return this.fcmService.onMessage().pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Initialize for web: set VAPID key and init FCM.
   */
  initWeb(vapidKey: string | null): void {
    if (this.isNative) return;
    this.fcmService.setVapidKey(vapidKey);
  }

  /**
   * Initialize FCM for web and request permission early (before login).
   */
  async initializeWeb(): Promise<void> {
    if (this.isNative) {
      console.log('[VendorPush] Skipping initializeWeb - running on native platform');
      return;
    }
    console.log('[VendorPush] Initializing FCM for web...');
    await this.fcmService.initialize();
    
    // Check current permission status first
    const currentPermission = typeof window !== 'undefined' && 'Notification' in window 
      ? Notification.permission 
      : 'denied';
    console.log('[VendorPush] Current notification permission status:', currentPermission);
    
    // Request permission early (before login) - better UX
    // Note: Browsers may block this if not in a user interaction context
    console.log('[VendorPush] Requesting notification permission for web...');
    try {
      const permission = await this.fcmService.requestNotificationPermission();
      console.log('[VendorPush] Notification permission result:', permission);
      
      // If permission is still 'default', set up fallback to request on first user interaction
      // Some browsers block permission requests without user gesture
      if (permission === 'default' && typeof window !== 'undefined') {
        console.log('[VendorPush] Permission still default - setting up fallback on first user interaction');
        this.setupPermissionRequestOnInteraction();
      }
    } catch (error) {
      console.error('[VendorPush] Error requesting notification permission:', error);
      // Set up fallback if initial request fails
      if (typeof window !== 'undefined') {
        this.setupPermissionRequestOnInteraction();
      }
    }
  }

  /**
   * Set up fallback to request permission on first user interaction.
   * This handles cases where browsers block permission requests without user gesture.
   */
  private setupPermissionRequestOnInteraction(): void {
    if (typeof window === 'undefined') return;
    
    const requestOnInteraction = async () => {
      console.log('[VendorPush] User interaction detected - requesting notification permission...');
      try {
        const newPermission = await this.fcmService.requestNotificationPermission();
        console.log('[VendorPush] Permission after user interaction:', newPermission);
      } catch (error) {
        console.error('[VendorPush] Error requesting permission on interaction:', error);
      }
      // Remove listeners after first interaction
      window.removeEventListener('click', requestOnInteraction);
      window.removeEventListener('touchstart', requestOnInteraction);
      window.removeEventListener('keydown', requestOnInteraction);
    };
    
    // Listen for various user interactions
    window.addEventListener('click', requestOnInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', requestOnInteraction, { once: true, passive: true });
    window.addEventListener('keydown', requestOnInteraction, { once: true, passive: true });
    
    console.log('[VendorPush] Set up permission request fallback on user interaction');
  }

  /**
   * Request permission (call from user action, e.g. banner button).
   * On native: shows system prompt if not yet granted, then registers.
   * On web: requests Firebase permission if not already granted. Returns token or null.
   */
  async requestPermission(): Promise<string | null> {
    if (this.isNative) {
      const permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === 'granted') {
        const token = this.nativeTokenSubject.value;
        if (token) return token;
        await this.registerNativePush();
        // Token arrives async; return null, backend registration happens via getTokenWhenAuthenticated
        return this.nativeTokenSubject.value;
      }
      if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
        const result = await PushNotifications.requestPermissions();
        if (result.receive === 'granted') {
          await this.registerNativePush();
        }
      }
      return this.nativeTokenSubject.value;
    }
    // For web: permission should already be requested in initializeWeb()
    // Just get the token if permission is granted
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const tokenOpts = { registerWithBackend: false as const };
      if (Notification.permission === 'granted') {
        return await this.fcmService.getToken(tokenOpts);
      } else if (Notification.permission === 'default') {
        // Permission not yet requested, request it now
        const permission = await this.fcmService.requestNotificationPermission();
        if (permission === 'granted') {
          return await this.fcmService.getToken(tokenOpts);
        }
      }
    }
    return null;
  }

  /**
   * Observable of push token when user is authenticated.
   * - Native: emits when we have profile AND token (token may arrive before or after login).
   * - Web: emits when FCM provides token after login.
   */
  getTokenWhenAuthenticated(): Observable<string | null> {
    if (this.isNative) {
      // Token often arrives before login; profile may load before or after — combine both.
      // IMPORTANT: Do not use distinctUntilChanged() on `null` alone — consecutive nulls with different
      // profile states (logged out vs logged in, still waiting for FCM) must both emit, or subscribers
      // never re-run after login and you only ever see "not logged in" logs.
      return combineLatest([this.profile$, this.nativeTokenSubject]).pipe(
        map(([profile, token]) => {
          const userId = profile?.id ?? null;
          return {
            userId,
            token: userId && token ? token : null,
          };
        }),
        distinctUntilChanged((a, b) => a.userId === b.userId && a.token === b.token),
        map((x) => x.token),
      );
    }
    // Backend registration is handled once by FcmBackendRegistrationService (AppComponent).
    return this.fcmService.initializeForUser({ registerWithBackend: false });
  }

  /** Check if notification permission is granted (for banner display). */
  async checkPermissionStatus(): Promise<'granted' | 'denied' | 'prompt' | 'default'> {
    if (this.isNative) {
      try {
        const status = await PushNotifications.checkPermissions();
        return status.receive === 'granted' ? 'granted' : status.receive === 'denied' ? 'denied' : 'prompt';
      } catch {
        return 'prompt';
      }
    }
    if (typeof Notification !== 'undefined') {
      return Notification.permission as 'granted' | 'denied' | 'prompt' | 'default';
    }
    return 'prompt';
  }
}
